import { signalCopy } from '../engine/buyingSignals';
import type { OrgConfig } from '../engine/types';

interface TurnReview {
  nodeId: string;
  optionText: string | null;
  tier: string | null;
  points: number;
  maxPoints: number;
  classificationSelected?: string;
  actualCategory?: string;
  classificationCorrect?: boolean;
  buyingSignal?: string;
  compliance?: string;
  explain?: { what: string; better: string };
  betterOption?: { text: string; explain: { what: string; better: string } };
}

interface ServerResult {
  personaScore: number;
  classificationScore: number;
  responseScore: number;
  handoffScore: number;
  finalBand: string;
  closeBand: string;
  outcome: string;
  passed: boolean;
  voided?: boolean;
  failureReasons: string[];
  complianceFailures: { nodeId: string; optionId: string; code: string }[];
  personaConfusion: { true: string; selected: string; severity: string } | null;
  assignedPersona: string;
  personaSelected: string | null;
  personaRevised: string | null;
  turnsReviewed: TurnReview[];
  rubricVersion: string;
  contentVersion: string;
}

const REASON: Record<string, string> = {
  persona_below_threshold: 'You did not read the prospect accurately enough',
  classification_below_threshold: 'You did not name what they were objecting to',
  response_below_threshold: 'Your answers were not strong enough',
  handoff_below_threshold: 'The handoff was not handled well enough',
  compliance_violation: 'You said something you are not permitted to say',
  close_band_below_minimum: 'The call did not end warm enough to transfer',
  severe_persona_confusion: 'You read the prospect as almost their opposite',
  attempt_cooldown: 'This attempt came too soon after the last one',
  attempt_cap_reached: 'You have used every attempt available',
};

const pct = (n: number) => `${Math.round(n * 100)}%`;

/**
 * §10.6 — all explanations appear in post-attempt review. Nothing here is read
 * from an answer key: the rep cannot read a summative key, so the server embeds
 * what they need in the record it wrote (§9.3, §8.6).
 */
export function SummativeReview({
  result, config, onDone,
}: {
  result: ServerResult;
  config: OrgConfig;
  onDone: () => void;
}) {
  const personaName = (id: string | null) =>
    config.personas.find((p) => p.id === id)?.name ?? id ?? '—';

  if (result.voided) {
    return (
      <main className="centered">
        <div className="card">
          <h1>Attempt not counted</h1>
          <p className="muted">{REASON[result.failureReasons[0] ?? ''] ?? 'This attempt was not scored.'}</p>
          <p className="small muted">
            It is on your record as taken, but it has not used one of your attempts.
          </p>
          <button className="primary" onClick={onDone}>Back to your progress</button>
        </div>
      </main>
    );
  }

  const subscores: [string, number, number][] = [
    ['Reading the prospect', result.personaScore, config.certification.passingPersonaScore],
    ['Naming the objection', result.classificationScore, config.certification.passingClassificationScore],
    ['What you said', result.responseScore, config.certification.passingResponseScore],
    ['The handoff', result.handoffScore, config.certification.passingHandoffScore],
  ];

  return (
    <main className="summary">
      <div className={`verdict ${result.passed ? 'pass' : 'fail'}`}>
        <h1>{result.passed ? 'Passed' : 'Not passed'}</h1>
        {result.passed ? (
          <p className="outcome-body">
            Your manager confirms the certification itself. Until they do, you are
            not cleared for live leads.
          </p>
        ) : (
          <ul className="reasons">
            {result.failureReasons.map((r) => <li key={r}>{REASON[r] ?? r}</li>)}
          </ul>
        )}
        <p className="muted small">
          {personaName(result.assignedPersona)} · {result.closeBand} at the transfer
          {' · '}rubric {result.rubricVersion}
        </p>
      </div>

      <section className="review">
        <h2>Your scores</h2>
        <ul className="review-list">
          {subscores.map(([label, score, threshold]) => (
            <li key={label} className={`review-item ${score >= threshold ? '' : 'sev-moderate'}`}>
              <span className="review-label">{label}</span>
              <span className={`pill ${score >= threshold ? 'pill-passed' : 'pill-severe'}`}>
                {pct(score)} · needs {pct(threshold)}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {result.complianceFailures.length > 0 && (
        <section className="review">
          <h2>What failed this on its own</h2>
          <ul className="review-list">
            {result.complianceFailures.map((f) => {
              // By node, not by code: one attempt can cross the same line twice
              // in different places, and each crossing is its own coaching moment.
              const turn = result.turnsReviewed.find((t) => t.nodeId === f.nodeId);
              return (
                <li key={`${f.nodeId}-${f.optionId}`} className="review-item sev-severe">
                  <span className="review-label">
                    <strong>“{turn?.optionText ?? f.optionId}”</strong>
                    {turn?.explain && <span className="small muted"> — {turn.explain.what}</span>}
                  </span>
                  <span className="pill pill-severe">{f.code.replace(/_/g, ' ')}</span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {result.personaConfusion && (
        <section className="review">
          <h2>How you read them</h2>
          <p className="muted">
            You read <strong>{personaName(result.personaConfusion.selected)}</strong>.
            They were <strong>{personaName(result.personaConfusion.true)}</strong>
            {result.personaRevised && ' — after you revised'}. From the moment you
            named them, you were offered the right moves for somebody else.
          </p>
        </section>
      )}

      <section className="review">
        <h2>Every turn</h2>
        <ul className="review-list transcript">
          {result.turnsReviewed.map((t, i) => (
            <li key={i} className={`review-item tier-${t.tier ?? 'none'}`}>
              <span className="review-label">
                <strong>{t.optionText ? `“${t.optionText}”` : 'You did not answer this one.'}</strong>
                {t.explain && <span className="small muted"> — {t.explain.what}</span>}
                {t.betterOption && (
                  <span className="better-pick small">
                    Better: “{t.betterOption.text}” — {t.betterOption.explain.what}
                  </span>
                )}
                {signalCopy(t.buyingSignal) && (
                  <span className="small muted"> They asked you {signalCopy(t.buyingSignal)}</span>
                )}
                {t.actualCategory && !t.classificationCorrect && (
                  <span className="small muted">
                    {' '}You handled <strong>{t.classificationSelected?.replace(/_/g, ' ')}</strong>;
                    they raised <strong>{t.actualCategory.replace(/_/g, ' ')}</strong>.
                  </span>
                )}
              </span>
              <span className={`pill pill-tier-${t.tier ?? 'bad'}`}>{t.points}/{t.maxPoints}</span>
            </li>
          ))}
        </ul>
      </section>

      <button className="primary wide" onClick={onDone}>Back to your progress</button>
    </main>
  );
}
